(() => {
  const ROOT_CLASS = 'ev-motion-ready';
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const revealObserver = REDUCED
    ? null
    : new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );

  const initReveal = (root = document) => {
    root.querySelectorAll('[data-ev-reveal]:not([data-ev-ready])').forEach((element, index) => {
      element.dataset.evReady = 'true';
      element.style.setProperty('--ev-delay', `${Math.min(index % 8, 7) * 55}ms`);
      if (REDUCED) element.classList.add('is-visible');
      else revealObserver?.observe(element);
    });
  };

  const initDepth = (root = document) => {
    if (REDUCED || !window.matchMedia('(pointer: fine)').matches) return;

    root.querySelectorAll('[data-ev-depth]:not([data-ev-depth-ready])').forEach((element) => {
      element.dataset.evDepthReady = 'true';

      const move = (event) => {
        const rect = element.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        element.style.setProperty('--ev-depth-x', `${x * -12}px`);
        element.style.setProperty('--ev-depth-y', `${y * -12}px`);
        element.style.setProperty('--ev-glow-x', `${(x + 0.5) * 100}%`);
        element.style.setProperty('--ev-glow-y', `${(y + 0.5) * 100}%`);
      };

      const reset = () => {
        element.style.setProperty('--ev-depth-x', '0px');
        element.style.setProperty('--ev-depth-y', '0px');
      };

      element.addEventListener('pointermove', move, { passive: true });
      element.addEventListener('pointerleave', reset, { passive: true });
    });
  };

  const portraitUrl = (source, width) => {
    const url = new URL(source, window.location.origin);
    url.searchParams.delete('width');
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    url.searchParams.set('width', String(width));
    url.searchParams.set('height', String(Math.round(width * 1.5)));
    url.searchParams.set('crop', 'center');
    return url.toString();
  };

  const upgradeCategoryImages = (root = document) => {
    root.querySelectorAll('.ev-category-card__image:not([data-ev-portrait-ready])').forEach((image) => {
      const source = image.getAttribute('src') || image.currentSrc;
      if (!source) return;

      image.dataset.evPortraitReady = 'true';
      image.srcset = [420, 640, 800, 1000]
        .map((width) => `${portraitUrl(source, width)} ${width}w`)
        .join(', ');
      image.sizes = '(min-width: 1100px) 16.66vw, (min-width: 750px) 33vw, 50vw';
      image.src = portraitUrl(source, 800);
      image.width = 800;
      image.height = 1200;
      image.decoding = 'async';
    });
  };

  const normalizeCommercialCopy = (root = document) => {
    const home = root.querySelectorr('.ev-home');

    if (home) {
      const shipping = home.querySelector('.ev-trust__item:first-child');
      const shippingTitle = shipping?.querySelector('strong');
      const shippingDetail = shipping?.querySelector('small');

      if (shippingTitle) shippingTitle.textContent = 'Free US shipping over $100';
      if (shippingDetail) shippingDetail.textContent = '$19.99 below $100 · tracked delivery';

      home
        .querySelectorAll('.ev-hero__actions .ev-button--gold, .ev-closing .ev-button--dark')
        .forEach((link) => link.setAttribute('href', '/collections/all'));
    }

    root.querySelectorAll('.product-information__content *').forEach((element) => {
      if (element.children.length > 0) return;
      const text = (element.textContent || '').trim();
      if (!/^(tax|taxes) included\.?$/i.test(text)) return;
      const wrapper =
        element.closest('tax-note, .product-tax, .taxes, .product__tax, .price__tax') || element;
      wrapper.setAttribute('data-evenlyr-tax-copy-hidden', 'true');
    });
  };

  const updateHeaderState = () => {
    const wrapper = document.querySelector('.page-wrapper');
    const top = Math.max(window.scrollY || 0, wrapper?.scrollTop || 0);
    document.body.classList.toggle('ev-header-scrolled', top > 24);
  };

  const init = (root = document) => {
    document.documentElement.classList.add(ROOT_CLASS);
    upgradeCategoryImages(root);
    initReveal(root);
    initDepth(root);
    normalizeCommercialCopy(root);
    updateHeaderState();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('scroll', updateHeaderState, { passive: true });
  document.querySelector('.page-wrapper')?.addEventListener('scroll', updateHeaderState, { passive: true });
})();